import { TRAINING_APPLY_STATUS_OPTIONS } from "@candidate/details/views/trainingOffer/followUp/list/logics";
import { TextFieldForm } from "@inputs/hook-form";
import { Divider, FormControlLabel, MenuItem, Stack, Switch, type Theme, Typography } from "@mui/material";
import type { FormEvent, MutableRefObject } from "react";
import { Controller, type UseFormReturn } from "react-hook-form";
import type { IModalTrainingApplyDisabledFields } from "..";
import { TRAINING_APPLY_EDITOR_SECTIONS } from "../../sharedForm/defaults";
import {
  TrainingSharedFormAbout,
  TrainingSharedFormContact,
  TrainingSharedFormDocuments,
  TrainingSharedFormEditorSections,
  TrainingSharedFormInformation,
} from "../../sharedForm/sections";
import type { ITrainingSharedFormFieldNames } from "../../sharedForm/types";
import type { IDocumentsActionsProgress } from "../logics/documents";
import type { IModalTrainingApplyForm } from "../types";

interface IModalTrainingApplyFormProps {
  disabledFields?: IModalTrainingApplyDisabledFields;
  formRef: MutableRefObject<HTMLFormElement | null>;
  hookForm: UseFormReturn<IModalTrainingApplyForm>;
  onSubmit: (event: FormEvent<HTMLFormElement>) => Promise<void>;
  loading: boolean;
  documentsProgress: IDocumentsActionsProgress | null;
}

const dividerStyled = (theme: Theme) => ({ backgroundColor: theme.palette.border.main });

const TRAINING_APPLY_FORM_FIELDS: ITrainingSharedFormFieldNames<IModalTrainingApplyForm> = {
  name: "name",
  period: "period",
  validation: "validation",
  wage: "wage",
  description: "description",
  trainingModalities: "trainingModalities",
  beneficiaries: "beneficiaries",
  financingModes: "financingModes",
  registrationModalities: "registrationModalities",
  url: "url",
  structureName: "structureName",
  address: "address",
  remote: "remote",
  structureWebsite: "structureWebsite",
  contact: "contact",
};

export const ModalTrainingApplyForm = ({
  formRef,
  hookForm,
  onSubmit,
  loading,
  documentsProgress,
  disabledFields,
}: IModalTrainingApplyFormProps) => {
  return (
    <form ref={formRef} onSubmit={onSubmit}>
      <Stack
        data-testid="training-apply-create-form"
        sx={{
          gap: 2,
        }}
      >
        <Stack
          sx={{
            gap: 2,
          }}
        >
          <Typography
            variant="subtitle2SemiBold"
            sx={{
              color: "primary.dark",
            }}
          >
            Statut de la candidature
          </Typography>
          <TextFieldForm
            control={hookForm.control}
            name="status"
            label="Statut de la candidature *"
            select
            disabled={disabledFields?.status}
            size="small"
            fullWidth
            sx={{ width: { xs: "100%", tablet: "50%" } }}
          >
            {TRAINING_APPLY_STATUS_OPTIONS.map((status) => (
              <MenuItem key={status.value} value={status.value}>
                {status.label}
              </MenuItem>
            ))}
          </TextFieldForm>
        </Stack>
        <Divider sx={dividerStyled} />
        <Typography
          variant="subtitle2SemiBold"
          sx={{
            color: "primary.dark",
          }}
        >
          Détails de la formation suivie
        </Typography>
        <Controller
          control={hookForm.control}
          name="createAssociatedOffer"
          render={({ field: { value, onChange } }) => (
            <FormControlLabel
              control={<Switch checked={value} onChange={(_, checked) => onChange(checked)} />}
              label="Souhaitez-vous créer une offre associée à cette formation suivie et la partager aux autres utilisateurs ?"
            />
          )}
        />
        <TrainingSharedFormAbout
          control={hookForm.control}
          fields={TRAINING_APPLY_FORM_FIELDS}
          nameLabel="Titre de l'offre *"
        />
        <Divider sx={dividerStyled} />
        <TrainingSharedFormEditorSections
          control={hookForm.control}
          fields={TRAINING_APPLY_FORM_FIELDS}
          sections={TRAINING_APPLY_EDITOR_SECTIONS}
        />
        <TrainingSharedFormInformation
          control={hookForm.control}
          fields={TRAINING_APPLY_FORM_FIELDS}
          structureNameLabel="Nom de la structure *"
          websiteLabel="Site internet"
          remoteLabel="La formation est dispensée à distance, sans lieu d’accueil physique (visio etc...)"
        />
        <TrainingSharedFormContact control={hookForm.control} fields={TRAINING_APPLY_FORM_FIELDS} />
        <Divider sx={dividerStyled} />
        <TrainingSharedFormDocuments
          control={hookForm.control}
          loading={loading}
          documentsProgress={documentsProgress}
        />
      </Stack>
    </form>
  );
};
